'use strict';

import { observable, flow, action, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import events from "@uw-it-sis/lib-react/lib/Events";
import {States} from "@uw-it-sis/lib-react/lib/AppConstants";
import {ScheduleBuilderEvents} from "./ScheduleBuilderConstants";
import api from "../lib/Api";
import {MetricLabels} from "@uw-it-sis/lib-react-myplan/lib/Constants";

class PinnedSchedulesStore {
    state = States.initial;

    showErrorModal = false;

    constructor() {
        makeObservable(this, {
            state: observable,
            showErrorModal: observable,
            closeErrorModal: action
        });

        this.url = 'api/schedule/schedules';
        this.termId = null;

        this.pinnedSchedules = [];
        this.analyticsStore = appStore.getAnalyticsStore();
        this.exceptionUtils = appStore.getExceptionUtils();
        this.httpClient = appStore.getHttpClient();
    }

    loadPinnedSchedules = flow(function* (termId, pinned, context) {
        this.state = States.pending;

        // Set termId if null or current termId is not the same as termId arg
        if (this.termId === null || this.termId !== termId) {
            this.termId = termId;
        }

        try {
            this.pinnedSchedules = yield api.getSavedSchedules(termId);

            if (appStore.isDevMode()) {
                console.log(`PinnedSchedulesStore: Data load complete for ${termId}`);
            }
            // List of current pinned schedule hashes
            let activityHashes = this.pinnedSchedules.map(schedule => schedule.activityHash);

            // Fire event for pinned schedules finished
            events.emit(ScheduleBuilderEvents.pinnedSchedulesLoaded, activityHashes);
            const labels = this.analyticsStore.label(MetricLabels.appContextName,context).get();
            let additionalData = {
                pin: pinned,
                noOfPinnedSchedule: this.getPinnedSchedules().length,
                context: context
            }
            this.analyticsStore.addEvent("SB-pinned-schedule",labels, additionalData);
            this.state = States.done;
        } catch(error) {
            this.exceptionUtils.handleException(error, "PinnedSchedulesStore.loadPinnedSchedules");
            this.state = States.error;
        }
    }.bind(this));

    addPinnedSchedule = flow(function* (data,context) {
        // Pinning schedules is limited to 3 per term
        if (this.pinnedSchedules.length >= 3) {
            this.showErrorModal = true;
        } else {
            this.state = States.pending;

            try {
                yield this.httpClient.doPost(`${this.url}/${this.termId}`, data);

                // Reload list of pinned schedules with updated data
                this.loadPinnedSchedules(this.termId, true, context);
            } catch(error) {
                this.exceptionUtils.handleException(error, "PinnedSchedulesStore.addPinnedSchedule");
                this.state = States.error;
            }
        }
    }.bind(this));

    deletePinnedSchedule = flow(function* (scheduleId, context) {
        this.state = States.pending;

        try {
            yield this.httpClient.doDelete(`${this.url}/${scheduleId}`);

            // Reload list of pinned schedules with updated data
            this.loadPinnedSchedules(this.termId, false, context);
        } catch(error) {
            this.exceptionUtils.handleException(error, "PinnedSchedulesStore.deletePinnedSchedule");
            this.state = States.error;
        }
    }.bind(this));

    getPinnedSchedules() {
        return this.pinnedSchedules;
    }

    closeErrorModal() {
        this.showErrorModal = false;
    }

    getSchedulePinnedData(hash) {
        // Build a list of hashes from list of pinned schedules
        let pinnedActivityHashes = this.pinnedSchedules.map(schedule => schedule.activityHash);
        // Find matching hash index
        let pinnedScheduleIndex = pinnedActivityHashes.indexOf(hash);
        // If matching pinned schedule found, return a map of pinned number and id (used for unpin action)
        if (pinnedScheduleIndex >= 0) {
            return {
                number: (pinnedScheduleIndex + 1),
                id: this.pinnedSchedules[pinnedScheduleIndex].id
            };
        } else {
            // return an empty object if no matching hash is found.
            return {};
        }
    }

    isPinned(hash) {
        // Build a list of hashes from list of pinned schedules
        let pinnedActivityHashes = this.pinnedSchedules.map(schedule => schedule.activityHash);
        // Find matching hash index
        let pinnedScheduleIndex = pinnedActivityHashes.indexOf(hash);

        return pinnedScheduleIndex >= 0;
    }
}

export default new PinnedSchedulesStore();